import { useState } from 'react';
import {
  deleteServiceRequest,
  fetchMessages,
  fetchServiceRequestActivityById,
  getServiceRequestById,
  saveScheduledAppointment,
  saveStatus,
  updateServiceRequestOneYearWarrantyIssueStatus,
  postMessage,
} from 'src/services/client';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { selectAuthenticatedUser } from 'src/store/selectors/auth';

interface UseServiceRequestAPIState {
  serviceRequest: any;
  activity: any[];
  messages: any[];
  loading: boolean;
  error: string | null;
  oneYearWarrantyIssues: any;
  suiteCounts: any;
  loadingMessages: boolean;
}

// Todo: Note, individual methods and state can be extracted out from here into their own unique custom hooks if need be in the future.
export const useServiceRequestAPI = () => {
  const user: any = useSelector((state: any) => selectAuthenticatedUser(state));

  const [state, setState] = useState<UseServiceRequestAPIState>({
    serviceRequest: null,
    activity: [],
    messages: [],
    loading: false,
    error: null,
    oneYearWarrantyIssues: null,
    suiteCounts: null,
    loadingMessages: false,
  });

  const setLoading = (loading: boolean) => {
    setState((prev: UseServiceRequestAPIState) => ({ ...prev, loading }));
  };

  const setLoadingMessages = (loadingMessages: boolean) => {
    setState((prev: UseServiceRequestAPIState) => ({
      ...prev,
      loadingMessages,
    }));
  };

  const setError = (error: string | null) => {
    setState((prev: UseServiceRequestAPIState) => ({ ...prev, error }));
  };

  const fetchServiceRequestById = async (id: string): Promise<void> => {
    try {
      setLoading(true);
      setError(null);

      const response: any = await getServiceRequestById(id);

      if (response.ok || response.status === 200) {
        const data = await response.json();
        return setState((prev: UseServiceRequestAPIState) => ({
          ...prev,
          serviceRequest: data,
          loading: false,
        }));
      }

      throw new Error('Failed to fetch service request');
    } catch (error) {
      console.error('Error fetching service request:', error);
      setError(error.message || 'Failed to fetch service request');
    } finally {
      setLoading(false);
    }
  };

  const fetchServiceRequestActivity = async (id: string): Promise<void> => {
    try {
      setLoading(true);

      const response: any = await fetchServiceRequestActivityById(id);

      if (response.ok || response.status === 200) {
        const activityData = await response.json();

        setState((prev: UseServiceRequestAPIState) => ({
          ...prev,
          activity: activityData || [],
          serviceRequest: prev.serviceRequest
            ? {
                ...prev.serviceRequest,
                activity: activityData || [],
              }
            : null,
        }));
      }

      throw new Error('Failed to fetch service request activity');
    } catch (error) {
      console.error('Error fetching service request activity:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMessagesFromAPI = async (referenceId: string): Promise<void> => {
    try {
      setLoadingMessages(true);

      const messageData = {
        ReferenceId: referenceId,
        ReferenceType: 'Ticket',
      };

      const response: any = await fetchMessages(messageData);

      if (response.ok || response.status === 200) {
        const data = await response.json();
        setState((prev: UseServiceRequestAPIState) => ({
          ...prev,
          messages: data || [],
        }));
      } else {
        console.warn('Failed to fetch messages');
      }
    } catch (error: any) {
      console.error('Error fetching messages:', error);
    } finally {
      setLoadingMessages(false);
    }
  };

  const postMessageToAPI = async (messageData: any): Promise<void> => {
    try {
      setLoadingMessages(true);

      const response: any = await postMessage(messageData);

      if (!response.ok && response.status !== 200) {
        throw new Error('Network request failed');
      }

      const responseData: any = await response.json();

      if (responseData?.statusCode === 200 && responseData.messages?.id) {
        return setState((prev: UseServiceRequestAPIState) => ({
          ...prev,
          messages: [...prev.messages, responseData.messages],
        }));
      }

      throw new Error('Invalid response data');
    } catch (error: any) {
      console.error('Error posting message:', error);

      const errorMessage: any =
        error.message === 'Invalid response data'
          ? '🚫 Message sending failed.'
          : '🚫 Something went wrong with sending message, Please try again.';

      toast.error(errorMessage, {
        autoClose: 3000,
        position: 'top-center',
      });
    } finally {
      setLoadingMessages(false);
    }
  };

  const refreshData = async (serviceRequestId: string): Promise<void> => {
    try {
      setLoading(true);
      setError(null);

      setState((prev: UseServiceRequestAPIState) => ({
        ...prev,
        serviceRequest: null,
        activity: [],
        messages: [],
      }));

      await fetchServiceRequestById(serviceRequestId);

      await Promise.all([
        fetchServiceRequestActivity(serviceRequestId),
        fetchMessagesFromAPI(serviceRequestId),
      ]);
    } catch (error: any) {
      console.error('Error refreshing data:', error);
      setError(error.message || 'Failed to refresh data');
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (
    statusData: any,
    serviceRequestId: string
  ): Promise<void> => {
    try {
      setLoading(true);
      setError(null);

      const response: any = await saveStatus(serviceRequestId, statusData);

      if (response.ok || response.status === 200) {
        const responseData = await response.json();

        toast.success('ⓘ Service request status has been updated.', {
          autoClose: 10000,
          position: 'top-center',
        });

        setState((prev: UseServiceRequestAPIState) => ({
          ...prev,
          serviceRequest: prev.serviceRequest
            ? {
                ...prev.serviceRequest,
                ...responseData.data,
              }
            : null,
        }));

        return await fetchServiceRequestActivity(serviceRequestId);
      }

      throw new Error('Failed to update status');
    } catch (error: any) {
      console.error('Error updating status:', error);
      setError(error.message || 'Failed to update status');

      toast.error(
        '🚫 Something went wrong with changing the status of the service request, please try again.',
        { autoClose: 12000, position: 'top-center' }
      );
    } finally {
      setLoading(false);
    }
  };

  const saveScheduledAppointmentFromAPI = async (
    formData: any,
    serviceRequestId: string
  ): Promise<void> => {
    try {
      setLoading(true);
      setError(null);

      const appointmentData = {
        ...formData,
        ticketId: serviceRequestId,
      };

      const response: any = await saveScheduledAppointment(appointmentData);

      if (response.ok || response.status === 200) {
        const responseData = await response.json();

        toast.success('ⓘ Appointment has been scheduled.', {
          autoClose: 10000,
          position: 'top-center',
        });

        setState((prev: UseServiceRequestAPIState) => ({
          ...prev,
          serviceRequest: prev.serviceRequest
            ? {
                ...prev.serviceRequest,
                ...responseData.data,
              }
            : null,
        }));

        return await fetchServiceRequestActivity(serviceRequestId);
      }

      throw new Error('Failed to save scheduled appointment');
    } catch (error: any) {
      console.error('Error saving scheduled appointment:', error);
      setError(error.message || 'Failed to save scheduled appointment');

      toast.error(
        '🚫 Something went wrong with adding a service request, please try again.',
        { autoClose: 12000, position: 'top-center' }
      );
    } finally {
      setLoading(false);
    }
  };

  const deleteServiceRequestFromAPI = async (
    serviceRequestId: string
  ): Promise<boolean> => {
    try {
      setLoading(true);
      setError(null);

      const response: any = await deleteServiceRequest(serviceRequestId);

      if (response.ok || response.status === 204) {
        toast.success('ⓘ The service request has been deleted.', {
          autoClose: 10000,
          position: 'top-center',
        });

        setState((prev: UseServiceRequestAPIState) => ({
          ...prev,
          serviceRequest: null,
          activity: [],
          messages: [],
        }));

        return true;
      }

      throw new Error('Delete request failed');
    } catch (error: any) {
      console.error('Error deleting service request:', error);
      setError(error.message || 'Failed to delete service request');

      toast.error(
        '🚫 Something went wrong with deleting the service request, please try again.',
        { autoClose: 12000, position: 'top-center' }
      );

      return false;
    } finally {
      setLoading(false);
    }
  };

  const updateServiceRequestOneYearWarrantyIssueStatusFromAPI = async (
    data: any
  ): Promise<void> => {
    try {
      setLoading(true);
      setError(null);

      const response: any =
        await updateServiceRequestOneYearWarrantyIssueStatus(data);

      if (response.ok || response.status === 200) {
        const responseData: any = await response.json();

        toast.success(
          'ⓘ Service request warranty issue status has been updated.',
          {
            autoClose: 10000,
            position: 'top-center',
          }
        );

        return setState((prev: UseServiceRequestAPIState) => ({
          ...prev,
          serviceRequest: prev.serviceRequest
            ? {
                ...prev.serviceRequest,
                ...responseData.data,
              }
            : null,
          oneYearWarrantyIssues: responseData.data.isOneYearWarrantyIssue,
        }));
      }

      throw new Error('Failed to update warranty issue status');
    } catch (error: any) {
      console.error('Error updating warranty issue status:', error);
      setError(error.message || 'Failed to update warranty issue status');

      toast.error(
        '🚫 Something went wrong with changing the status of warranty issue of the service request, please try again.',
        { autoClose: 12000, position: 'top-center' }
      );
    } finally {
      setLoading(false);
    }
  };

  const clearServiceRequestState = () => {
    setState((prev: UseServiceRequestAPIState) => ({
      ...prev,
      serviceRequest: null,
      activity: [],
      messages: [],
    }));
  };

  return {
    serviceRequest: state.serviceRequest,
    activity: state.activity,
    messages: state.messages,
    loading: state.loading,
    error: state.error,
    oneYearWarrantyIssues: state.oneYearWarrantyIssues,
    loadingMessages: state.loadingMessages,
    fetchServiceRequestById,
    fetchServiceRequestActivity,
    fetchMessagesFromAPI,
    refreshData,
    updateStatus,
    saveScheduledAppointmentFromAPI,
    deleteServiceRequestFromAPI,
    updateServiceRequestOneYearWarrantyIssueStatusFromAPI,
    postMessageToAPI,
    clearServiceRequestState,
  };
};
